import { WatchQueryFetchPolicy } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { isEqual, omit } from 'lodash-es';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { CandidateDocument } from '@pflegia/data-access/candidate.generated';
import { CandidateFilterType } from '@pflegia/types/api/candidate/candidate-filter-type';
import { CandidatePriority } from '@pflegia/types/api/candidate/candidate-priority';
import { FilterValue } from '@pflegia/types/api/job-board/filter-value';
import { ITEMS_PER_PAGE } from '@pflegia/types/common/job-board/constants';
import { PaginationSortField } from '@pflegia/types/common/job-position/pagination-sort-field';
import { LocationObject } from '@pflegia/types/common/location/location-object';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useJobListSortStore } from '@pflegia/ui/hooks/use-sort';
import { JOB_BOARD, JOBS } from '@pflegia/ui/pflegia/constants/routes';
import { isCityStateLocationObject, isStateOnlyLocationObject } from '@pflegia/util/common/location';

import { filterValuesToGuestFilters } from '@domains/job-board/utils/filter-values-to-guest-filters';

import { useRouterHelper } from '@shared/hooks/use-router-helper';

import { FiltersDto } from '../dto/filters-dto';
import { useGetPotentialJobs } from '../hooks/use-get-potential-jobs';
import { buildJobsListingPath } from '../utils/build-jobs-listing-path';
import { parseJobsPageFromSlugs } from '../utils/parse-jobs-page-from-slugs';
import { useFiltersState } from './use-filter-state.hook';
import { useSideBarState } from './use-side-bar-state.hook';

export const useJobListState = (
  minimumFilters = false,
  perPage = ITEMS_PER_PAGE,
  {
    overrideInitialFilters,
    isJobPositionListQuerySkippedExternally = false,
    routeLocation,
  }: {
    overrideInitialFilters?: FilterValue[];
    isJobPositionListQuerySkippedExternally?: boolean;
    routeLocation?: LocationObject;
  } = {},
) => {
  const router = useRouter();
  const { isMinifiedJobBoard, isTransparentJobBoard } = useRouterHelper();
  const { page, filterSlugs } = parseJobsPageFromSlugs(router.query.slugs as string | string[] | undefined);
  const sort = useJobListSortStore(state => state.sort);
  const isAuthenticated = useAuth();
  const filterState = useFiltersState({ minimumFilters });
  const { isSeoLandingPage } = useRouterHelper();
  const { data } = useQuery(CandidateDocument, { skip: !isAuthenticated || isSeoLandingPage });

  const { isLoading: filterMutationLoading, updateCandidateFilters } = useSideBarState();

  const skipInitialQuery = useRef(Boolean(overrideInitialFilters?.length));
  const prevFilters = useRef<FilterValue[]>([]);
  const skipFiltersUpdate = useRef(false);
  const isResettingPage = useRef(false);

  const queryVariableFilters = useMemo(
    () => filterValuesToGuestFilters(filterState.filters, data?.candidate?.priority as CandidatePriority),
    [data?.candidate?.priority, filterState.filters],
  );

  // For the filter changes to pause the jobs query until the shallow URL sync finishes,
  // otherwise Apollo can fire once for the local filter update and again for the new route.
  const shouldSkipJobPositionListQuery = useMemo(() => {
    const filterDto = FiltersDto.create(filterState.filters);
    const prevNonPendingPositions = FiltersDto.create(prevFilters.current);
    const hasNewValidFilterPositions = filterDto.hasPositionsToGetPotentialJobs(prevNonPendingPositions);

    return (
      skipInitialQuery.current ||
      isJobPositionListQuerySkippedExternally ||
      isResettingPage.current ||
      !filterState.hasRequiredFilters() ||
      filterMutationLoading ||
      filterState.isLoading ||
      (!filterState.filtersFullyInitialized && (!overrideInitialFilters?.length || !hasNewValidFilterPositions))
    );
  }, [filterMutationLoading, filterState, isResettingPage, overrideInitialFilters?.length, isJobPositionListQuerySkippedExternally]);

  const locationFilterValues = filterState.filters
    .filter(filter => filter.type === CandidateFilterType.Location)
    .map(filter => filter.locationObject);
  const isStateOnlySearch = filterState.filtersFullyInitialized
    ? locationFilterValues.length > 0 && locationFilterValues.every(location => isStateOnlyLocationObject(location))
    : isStateOnlyLocationObject(routeLocation);
  const isCityStateSearch = filterState.filtersFullyInitialized
    ? locationFilterValues.length > 0 && locationFilterValues.every(location => isCityStateLocationObject(location))
    : isCityStateLocationObject(routeLocation);
  const isInitialCityStateDefault = isCityStateSearch && !filterState.filtersFullyInitialized;
  const routeSort = isStateOnlySearch || isInitialCityStateDefault ? PaginationSortField.Salary : sort;

  const jobPositionListVariables = useMemo(
    () => ({
      page: page - 1,
      perPage,
      sortField: routeSort,
      sortOrder: routeSort === PaginationSortField.Distance ? 'ASC' : 'DESC',
      ...(!isAuthenticated && { filters: queryVariableFilters }),
      ...(isMinifiedJobBoard && { isMinifiedJobBoard }),
      ...(isTransparentJobBoard && { isTransparentJobBoard }),
    }),
    [isAuthenticated, isMinifiedJobBoard, isTransparentJobBoard, page, perPage, queryVariableFilters, routeSort],
  );

  const jobPositionListFetchPolicy: WatchQueryFetchPolicy = isAuthenticated ? 'network-only' : 'cache-first';
  const jobPositionListQueryOptions = useMemo(
    () => ({
      fetchPolicy: jobPositionListFetchPolicy,
      variables: jobPositionListVariables,
      skip: shouldSkipJobPositionListQuery,
    }),
    [jobPositionListFetchPolicy, jobPositionListVariables, shouldSkipJobPositionListQuery],
  );

  const jobPositionListQuery = useGetPotentialJobs(jobPositionListQueryOptions);

  const isLoading = useMemo(
    () => jobPositionListQuery.loading || filterMutationLoading || filterState.isLoading,
    [filterMutationLoading, filterState.isLoading, jobPositionListQuery.loading],
  );

  const shouldUpdateFilters = useMemo(() => {
    const filterDto = FiltersDto.create(filterState.filters);
    return (
      !isLoading &&
      filterState.filtersFullyInitialized &&
      !isEqual(filterState.filters, prevFilters.current) &&
      filterDto.hasMinimumFiltersToSyncProfile()
    );
  }, [isLoading, filterState.filters, filterState.filtersFullyInitialized]);

  const handleSetPage = useCallback(
    (pageNumber: number) => {
      const hash = router.asPath.split('#')[1] || '';
      const nextSlugs = pageNumber > 1 ? [...filterSlugs, String(pageNumber)] : filterSlugs;

      return router.push(
        { pathname: router.pathname, query: { ...omit(router.query, ['page']), slugs: nextSlugs }, hash },
        { pathname: buildJobsListingPath({ basePathname: isTransparentJobBoard ? JOBS : JOB_BOARD, filterSlugs, page: pageNumber }), hash },
        { shallow: true },
      );
    },
    [router, filterSlugs, isTransparentJobBoard],
  );

  const applyFilters = useCallback(
    async (
      filters: FilterValue[],
      {
        isPageResetHandledExternally = false,
      }: {
        isPageResetHandledExternally?: boolean;
      } = {},
    ) => {
      if (isLoading || isEqual(filters, filterState.filters)) {
        return;
      }
      if (page !== 1 && !isPageResetHandledExternally) {
        isResettingPage.current = true;
        await handleSetPage(1);
      }
      isResettingPage.current = false;
      filterState.setFilters(filters);
    },
    [isLoading, filterState, page, handleSetPage],
  );

  useEffect(() => {
    if (skipInitialQuery.current && overrideInitialFilters?.length) {
      filterState.setFilters(overrideInitialFilters, true);
      skipInitialQuery.current = false;
    }
  }, [filterState, overrideInitialFilters]);

  useEffect(() => {
    if (filterState.filtersFullyInitialized && prevFilters.current.length === 0) {
      prevFilters.current = [...filterState.filters];
    }
  }, [filterState.filtersFullyInitialized, filterState.filters]);

  useEffect(() => {
    if (isSeoLandingPage || !shouldUpdateFilters) return;

    if (skipFiltersUpdate.current) {
      skipFiltersUpdate.current = false;
      return;
    }

    if (isAuthenticated) {
      updateCandidateFilters(filterState.filters);
    }

    prevFilters.current = [...filterState.filters];
  }, [isAuthenticated, filterState.filters, handleSetPage, isSeoLandingPage, shouldUpdateFilters, updateCandidateFilters]);

  return {
    isLoading,
    page,
    setPage: handleSetPage,
    filterState,
    jobPositionListQuery,
    applyFilters,
    queryVariableFilters,
    isStateOnlySearch,
    isCityStateSearch,
  };
};
